import React, { Component, Fragment } from 'react'
import Media from 'react-media'
import styled from 'styled-components'
import sortBy from 'array-sort-by'
import { PieChart, Pie, Sector, Cell } from 'recharts'
import { connect } from 'react-redux'
import { overviewActions, productMapActions } from '../actions'
import { pxToRem, color } from '../utils'
import Header from '../components/Header'
import Sidebar from '../components/Sidebar'
import { Grid, Content } from '../components/Layout'
import DataTable from '../components/Table/Table'
import ResponsiblesContent from '../components/ResponsiblesContent'
import Modal from '../components/Modal'
import { RefreshIcon } from '../components/icons'
import { Button } from '../components/Form'

const warningMessage = process.env.REACT_APP_OVERVIEW_WARNING_MESSAGE || ''

const overviewGridItem = ({ columns, gutter }) => {
  return `
  max-width: calc(${100 / columns}% - ${pxToRem(gutter * 2)});
  min-width: calc(${100 / columns}% - ${pxToRem(gutter * 2)});
  flex: 1 1 calc(${100 / columns}% - ${pxToRem(gutter * 2)});
  `
}

const Container = styled.div`
  background: #fff;
  border-radius: ${pxToRem(7)};
  padding: ${pxToRem(24)};
  display: flex;
  flex-wrap: wrap;
  align-items: center;

  @media only screen and (max-width: 760px) {
    flex-direction: column;
  }
`
const ContentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${pxToRem(35)};
  h1,
  h2,
  strong {
    margin: 0;
    padding: 0;
  }
  h2 {
    font-size: ${pxToRem(16)};
    font-style: italic;
    margin-top: ${pxToRem(5)};
  }
  h1 {
    font-size: ${pxToRem(21)};
  }
  strong {
    font-size: ${pxToRem(16)};
  }
  h2#warning-message {
    margin: 10px 0 5px 0;
    font-style: normal;
    color: #f44336;
  }

  div:last-child {
    text-align: left;
    span {
      text-transform: capitalize;
      display: block;
      color: ${color.secondary};
      margin-top: ${pxToRem(5)};
    }
  }

  @media only screen and (max-width: 700px) {
    h2 {
      font-size: ${pxToRem(13)};
    }
    h1 {
      font-size: ${pxToRem(17)};
    }
    strong {
      font-size: ${pxToRem(15)};
    }
  }

  @media only screen and (max-width: 550px) {
    flex-direction: column;
    div:last-child {
      text-align: left;
      margin-top: ${pxToRem(20)};
    }
  }
`

const OverviewEmpty = styled.div`
  color: #5b666d;
  min-height: ${pxToRem(240)};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  div {
    text-align: center;
  }
  strong {
    font-size: ${pxToRem(14)};
    text-align: center;
  }
`

const ReloadButton = styled(Button)`
  background: transparent;
  font-size: ${pxToRem(12)};
  padding: ${pxToRem(8)} ${pxToRem(15)};
  display: flex;
  border-color: ${color.primary};
  color: ${color.primary};
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin: ${pxToRem(15)} auto ${pxToRem(10)};
  svg {
    margin-left: ${pxToRem(10)};
  }
`

const ProductLink = styled.a`
  color: ${color.secondary};
  text-transform: uppercase;
  text-decoration: underline;
  font-weight: bold;
  cursor: pointer;
`

const ModalLayout = styled.div`
  display: flex;
  padding-left: ${pxToRem(20)};
  padding-right: ${pxToRem(20)};

  > div:first-child {
    padding-top: ${pxToRem(20)};
    padding-bottom: ${pxToRem(20)};
    width: 180px;
  }

  > div:last-child {
    flex: 1;
    padding: ${pxToRem(30)};
    padding-top: ${pxToRem(20)};
    padding-bottom: ${pxToRem(20)};
    border-left: 1px solid #d3dce2;
    margin-left: ${pxToRem(16)};
  }

  > div > p > h3 {
    text-transform: uppercase;
    font-size: ${pxToRem(14)};
  }

  > div > p {
    font-size: ${pxToRem(14)};
  }

  @media only screen and (max-width: 700px) {
    flex-direction: column;
    > div:last-child {
      border: none;
      padding-left: 0;
      margin-left: 0;
    }
  }
`

const DefinitionList = styled.dl`
  margin: 2em 0;
  &:first-child {
    margin-top: 0;
  }
  dd,
  dt {
    padding: 0;
    margin: 0;
    font-size: ${pxToRem(14)};
  }
  dd {
    span,
    strong {
      color: ${color.secondary};
    }
    strong {
      font-weight: bold;
    }
  }
  dt {
    text-transform: uppercase;
    font-weight: bold;
    margin-bottom: 1em;
  }
  & > dl {
    overflow: auto;
    margin-top: 0;
    max-height: 260px;
  }
  dl {
    &:first-child {
      margin-top: 0;
    }
    dt {
      color: ${color.primary};
      margin-bottom: 0;
      font-weight: bold;
    }
  }
`
const ProductPhoto = styled.img`
  width: ${pxToRem(180)};
  display: block;
  margin-bottom: ${pxToRem(17)};
`

const AttributeLabel = styled.h3`
  font-size: ${pxToRem(15)};
  color: ${color.secondary};
`

const Attribute = styled.div`
  font-size: ${pxToRem(13)};
  margin-bottom: ${pxToRem(10)};
`

const Kinds = styled.div`
  max-height: ${pxToRem(300)};
  min-width: ${pxToRem(250)};
  align-content: row;
  overflow: auto;
  align-items: center;
  justify-content: center;
  flex: 1;

  div {
    max-width: ${pxToRem(1200)};
    display: flex;
    flex-wrap: wrap;
  }

  @media only screen and (max-width: 450px) {
    max-height: 100%;
    max-width: 100%;
    div {
      margin: 0 ${pxToRem(10)};
    }
  }
`

const MiniBox = styled.button`
  cursor: pointer;
  background-color: #fff;
  border: 1px solid #d6d6d6;
  border-radius: ${pxToRem(7)};
  padding: ${pxToRem(15)} 0;
  height: ${pxToRem(80)};
  display: inline-flex;
  margin: ${pxToRem(5)};
  justify-content: center;
  flex-direction: column;
  align-items: center;
  outline: 0;

  ${overviewGridItem({ columns: 6, gutter: 5 })} ${props => {
    if (props.active) {
      return `
        box-shadow: ${pxToRem('-1px')} ${pxToRem('0px')} ${pxToRem('10px')} ${pxToRem(
        '5px'
      )} rgba(148, 148, 148, 0.13);
        outline: 0;
        background-color: #fafcff;
      `
    }

    return ''
  }}

  strong {
    font-size: ${pxToRem(12)};
    text-transform: uppercase;
    font-weight: bold;
    display: block;
    justify-content: center;
    margin: ${pxToRem(10)} auto;
  }

  span {
    font-size: ${pxToRem(12)};
    display: block;
    margin: 0 auto;
    font-weight: bold;

    ${props => {
    return `
        color: ${props.color}
      `
  }};
  }

  @media only screen and (max-width: 1500px) {
    ${overviewGridItem({ columns: 5, gutter: 5 })};
  }

  @media only screen and (max-width: 1300px) {
    ${overviewGridItem({ columns: 4, gutter: 5 })};
  }

  @media only screen and (max-width: 1190px) {
    ${overviewGridItem({ columns: 3, gutter: 5 })};
  }

  @media only screen and (max-width: 1040px) {
    ${overviewGridItem({ columns: 2, gutter: 5 })};
  }

  @media only screen and (max-width: 450px) {
    ${overviewGridItem({ columns: 1, gutter: 5 })};
  }
`

const Icon = styled.div`
  content: '';
  display: block;
  align-self: center;
  width: ${pxToRem(10)};
  height: ${pxToRem(10)};
  border-radius: 50%;
  margin: 0 auto;

  ${props => {
    return `
      background-color: ${props.color}
      box-shadow: 0 0 10px ${props.color};
    `
  }};
`

const Graphic = styled.div`
  width: ${pxToRem(450)};
  display: inline-flex;
  justify-content: center;
  margin: 0 ${pxToRem(10)};

  @media only screen and (max-width: 760px) {
    width: auto;
    order: -1;
  }
`

const List = styled.div`
  width: 100%;
`

const ListLink = styled.a`
  color: ${color.secondary};
  text-transform: uppercase;
  text-decoration: underline;
  font-weight: bold;
  cursor: pointer;
  display: inline-block;
  margin: ${pxToRem(20)} 0;
  font-size: ${pxToRem(13)};
`

const colors = [
  '#E57373',
  '#9575CD',
  '#29B6F6',
  '#81C784',
  '#FF7043',
  '#78909C',
  '#EC407A',
  '#AB47BC',
  '#26C6DA',
  '#4DB6AC',
  '#7CB342',
  '#795548',
  '#F9A825',
  '#FF7043',
  '#42A5F5',
  '#26A69A',
  '#4CAF50',
  '#BDBDBD'
]

const renderActiveShape = props => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload } = props

  const productStyle = {
    fontSize: pxToRem('24px')
  }

  const kindNameStyle = {
    fontWeight: 'bold'
  }

  return (
    <g>
      <text x={cx} y={cy} dy={-5} textAnchor="middle" fill="#5b666d" style={productStyle}>
        {payload.count} produtos
      </text>

      <text x={cx} y={cy} dy={18} textAnchor="middle" fill={fill} style={kindNameStyle}>
        {payload.name}
      </text>

      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />

      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 6}
        outerRadius={outerRadius + 10}
        fill={fill}
      />
    </g>
  )
}

const plural = (text, ...args) => {
  const [qtd = '--'] = args

  const formatedText = text.map((item, index) => {
    switch (qtd) {
      case 0:
        item = index === 0 ? 'Não existem ' : item
        return item
      case 1:
        item = index === 0 ? `Existe no total ${qtd} ` : 'produto'
        return item
      default:
        item = index === 0 ? `Existem no total ${qtd} ` : 'produtos'
        return item
    }
  })

  return formatedText
}

class Overview extends Component {
  constructor(props) {
    super(props)
    this.toggleKind = this.toggleKind.bind(this)

    this.state = {
      activeIndex: 0,
      activeKind: 0,
      isHidden: true,
      openProductMapModal: false
    }

    this.columns = {
      id: {
        hide: true
      },
      name: {
        label: 'Produto',
        sortable: true,
        width: 150
      },
      areas: {
        label: 'Area',
        sortable: true,
        width: 150
      }
    }
  }

  componentDidMount() {
    this.props.fetchOverview(this.props.areaId)
  }

  updateActiveKind = ({ index: activeKind }) => {
    this.setState({
      activeKind
    })
  }

  toggleKind = id => {
    const match = this.props.kindId === id

    this.setState({
      isHidden: match
    })
    this.props.toggleProductsTable(match)
  }

  renderGraphic = ({ mobile, orderedOverview, activeKind, renderActiveShape }) => {
    const [innerRadius, outerRadius, size] = mobile ? [77, 90, 250] : [110, 130, 300]

    return (
      <PieChart width={size} height={size}>
        <Pie
          data={orderedOverview}
          activeIndex={activeKind}
          activeShape={renderActiveShape}
          startAngle={450}
          endAngle={90}
          dataKey="count"
          nameKey="name"
          cx={size / 2}
          cy={size / 2}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
        >
          {orderedOverview.map((item, index) => <Cell key={index} fill={item.color} />)}
        </Pie>
      </PieChart>
    )
  }

  handledData = () => {
    const { columns } = this
    const { products, shouldDisplayArea } = this.props

    if (!shouldDisplayArea) {
      delete columns.areas
      delete products.areas
    }

    return { columns, products }
  }

  handleColors = index => {
    const size = colors.length
    const multiplier = Math.floor(index / size)

    if (index >= size) {
      return colors[index - size * multiplier]
    }

    return colors[index]
  }

  closeModal = () => {
    this.setState(state => ({
      openProductMapModal: false
    }))
  }

  openModal = (e, id) => {
    e.preventDefault()
    this.props.fetchProductMap(id)

    this.setState(state => ({
      openProductMapModal: true
    }))
  }

  render() {
    const {
      updateActiveKind,
      handledData,
      handleColors,
      closeModal,
      openModal,
      columns
    } = this

    const {
      menuItems = [],
      router,
      total_records,
      overview,
      fetch_errors,
      fetchOverviewList,
      updateKind,
      areaId,
      kindId,
      resetOverviewList,
      fetchingProducts,
      fetchOverview,
      productMap,
      fetchingProductMap,
      shouldDisplayArea,
      showingProductsTable,
      toggleProductsTable
    } = this.props

    const { activeKind, isHidden, openProductMapModal } = this.state

    const CellComponent = ({ name, value, row, ...rest }) => {
      return (
        <ProductLink {...rest} onClick={e => openModal(e, row.id)}>
          {value}
        </ProductLink>
      )
    }

    columns['name'] = {
      ...columns['name'],
      CellComponent
    }

    const orderedOverview = sortBy(overview, item => [-item.percent, item.name]).map(
      (item, index) => {
        item['color'] = handleColors(index)
        return item
      }
    )

    return (
      <Fragment>
        <Grid>
          <Header router={router} />
          <Sidebar menuItems={menuItems} router={router} />
          <Content>
            <ContentHeader>
              <div>
                <h1>Panorama Geral de Produtos Químicos</h1>
                <h2>
                  <strong>{plural`Existe no total ${total_records} produto`}</strong>
                </h2>
                <h2 id="warning-message">{warningMessage}</h2>
              </div>
            </ContentHeader>

            {fetch_errors.overview ? (
              <OverviewEmpty>
                <div>
                  <strong>Falha ao carregar as informações.</strong>
                  <br />

                  <ReloadButton
                    onClick={() => {
                      fetchOverview(areaId)
                    }}
                  >
                    Tentar novamente{' '}
                    <RefreshIcon></RefreshIcon>
                  </ReloadButton>
                </div>
              </OverviewEmpty>
            ) : (
              <Fragment>
                {(orderedOverview || []).length ? (
                  <Fragment>
                    <Container>
                      <Kinds>
                        <div>
                          {orderedOverview.map((item, index) => (
                            <MiniBox
                              key={item.id}
                              active={item.id === kindId}
                              color={item.color}
                              onClick={() => {
                                updateActiveKind({ index })
                                updateKind(item.id)

                                this.toggleKind(item.id)
                              }}
                            >
                              <div>
                                <Icon color={item.color} />
                              </div>
                              <strong>{item.name}</strong>
                              <span> {Math.floor(item.percent * 100)}%</span>
                            </MiniBox>
                          ))}
                        </div>
                      </Kinds>

                      <Graphic>
                        <Media query="(max-width: 450px)">
                          {matches =>
                            this.renderGraphic({
                              mobile: matches,
                              orderedOverview,
                              activeKind,
                              renderActiveShape
                            })
                          }
                        </Media>
                      </Graphic>
                    </Container>

                    <List>
                      {!isHidden && (
                        <Fragment>
                          <ListLink
                            onClick={() => {
                              if (showingProductsTable) {
                                toggleProductsTable(false).then(resetOverviewList)
                              } else {
                                toggleProductsTable(true).then(() => {
                                  fetchOverviewList(areaId, kindId)
                                })
                              }
                            }}
                          >
                            {showingProductsTable ? 'Ocultar listagem' : 'Ver listagem'}
                          </ListLink>

                          {!showingProductsTable ? null : (
                            <DataTable
                              loading={fetchingProducts}
                              error={fetch_errors.list}
                              fetchData={() => {
                                fetchOverviewList(areaId, kindId)
                              }}
                              columns={handledData().columns}
                              height={900}
                              width={1100}
                              data={handledData().products}
                            />
                          )}
                        </Fragment>
                      )}
                    </List>
                  </Fragment>
                ) : (
                  <OverviewEmpty>
                    <div>
                      <strong>Não há informações.</strong>
                    </div>
                  </OverviewEmpty>
                )}
              </Fragment>
            )}
            <ResponsiblesContent />
          </Content>
          <Modal
            title={productMap.name}
            safeContent={false}
            loading={fetchingProductMap.productMap}
            open={openProductMapModal}
            size={'large'}
            error={fetch_errors.productMap}
            closeMethod={closeModal}
          >
            <ModalLayout>
              <div>
                {productMap.photo_path && <ProductPhoto src={productMap.photo_path} />}
                {productMap.document_path && (
                  <Button link href={productMap.document_path} target="_blank">
                    Gerar Fds's
                  </Button>
                )}
                <DefinitionList>
                  <dt>Qtd. Máxima de Armazenamento</dt>
                  <dd>
                    <span>{productMap.maximum_storage ? productMap.maximum_storage : '---'}</span>
                  </dd>
                </DefinitionList>
                <DefinitionList>
                  <dt>Id. de Perigo</dt>
                  <dd>
                    <span>{productMap.kinds ? productMap.kinds : '---'}</span>
                  </dd>
                </DefinitionList>
              </div>
              <div>
                {productMap.ppe && (
                  <div>
                    <AttributeLabel>EPI/EPC</AttributeLabel>
                    <Attribute dangerouslySetInnerHTML={{ __html: productMap.ppe }} />
                    <Attribute dangerouslySetInnerHTML={{ __html: productMap.ppe_observation }} />
                  </div>
                )}
                {productMap.storage && (
                  <div>
                    <AttributeLabel>Armazenamento</AttributeLabel>
                    <Attribute dangerouslySetInnerHTML={{ __html: productMap.storage }} />
                  </div>
                )}
                {productMap.first_aid && (
                  <div>
                    <AttributeLabel>Primeiros Socorros</AttributeLabel>
                    <Attribute dangerouslySetInnerHTML={{ __html: productMap.first_aid }} />
                  </div>
                )}
                {productMap.ambiental_danger && (
                  <div>
                    <AttributeLabel>Risco Ambiental</AttributeLabel>
                    <Attribute dangerouslySetInnerHTML={{ __html: productMap.ambiental_danger }} />
                  </div>
                )}
                {productMap.product_spill && (
                  <div>
                    <AttributeLabel>Derramamento</AttributeLabel>
                    <Attribute dangerouslySetInnerHTML={{ __html: productMap.product_spill }} />
                  </div>
                )}
                {productMap.incompatibility && (
                  <div>
                    <AttributeLabel>Incompatibilidade</AttributeLabel>
                    <Attribute dangerouslySetInnerHTML={{ __html: productMap.incompatibility }} />
                  </div>
                )}
                {productMap.areas &&
                  shouldDisplayArea && (
                    <div>
                      <AttributeLabel>Áreas</AttributeLabel>
                      <Attribute dangerouslySetInnerHTML={{ __html: productMap.areas }} />
                    </div>
                  )}
              </div>
            </ModalLayout>
          </Modal>
        </Grid>
      </Fragment>
    )
  }
}

const mapStateToProps = state => ({
  products: state.overview.products,
  fetchingProducts: state.overview.fetching.list,
  areaId: state.auth.areaId,
  overview: state.overview.area_kinds,
  total_records: state.overview.total_records,
  fetching: state.overview.fetching,
  fetch_errors: state.overview.fetch_errors,
  kindId: state.overview.kindId,
  shouldDisplayArea: state.auth.canChangeArea,
  productMap: state.productMap.productMap,
  fetchingProductMap: state.productMap.fetching,
  showingProductsTable: state.overview.showingProductsTable
})

const mapDispatchToProps = dispatch => {
  return {
    fetchOverview: areaId => {
      dispatch(overviewActions.fetchOverview(areaId))
    },
    fetchOverviewList: (areaId, kind_id) => {
      dispatch(overviewActions.fetchOverviewList(areaId, kind_id))
    },
    toggleProductsTable: (value) => {
      return dispatch(overviewActions.toggleProductsTable(value))
    },
    fetchProductMap: id => {
      dispatch(productMapActions.fetchProductMap(id))
    },
    updateKind: kindId => {
      dispatch(overviewActions.updateKind(kindId))
    },
    resetOverviewList: () => {
      dispatch(overviewActions.resetList())
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Overview)



// WEBPACK FOOTER //
// ./src/containers/Overview.js